import type { CSSProperties, ReactNode } from 'react';
import React, { forwardRef } from 'react';

import { Box } from '@mui/material';

import {
  ArrowRightBigIcon,
  ErrorOutlineIcon,
  GoogleSyncSmallIcon,
  InfoIcon,
  RoundOutlineDoneIcon,
  WarningIcon,
} from '@setup/svg-icons/svg-icons';

import { CustomAlertStyle } from './MuiAlert.style';

import type { AlertProps } from '@mui/material';

const variantIcon: { [key: string]: ReactNode } = {
  info: <InfoIcon />,
  warning: <WarningIcon />,
  error: <ErrorOutlineIcon />,
  success: <RoundOutlineDoneIcon />,
  'google-sync': <GoogleSyncSmallIcon />,
};

/**
 * Additional properties for the MuiAlert component.
 * The type of icon to be displayed. Defaults to 'info'.
 */
type AdditionalProps = {
  iconType?: 'info' | 'warning' | 'error' | 'success' | 'google-sync';
  isLink?: boolean;
  alignSelfIcon?: CSSProperties['alignSelf'];
};
/**
 * Props for the MuiAlert component, extending from MUI's `AlertProps`.
 * more detailed information in the documentation  ->
 * https://mui.com/material-ui/react-alert/
 * https://mui.com/material-ui/api/alert/
 */
type PropsType = AlertProps & AdditionalProps;

/**
 * A customizable alert component with optional icon display.
 *
 * @param {PropsType} props - Props for configuring the MuiAlert component.
 * @param {React.ForwardedRef<HTMLDivElement>} ref - Reference to the root HTMLDivElement of the component.
 * @returns {JSX.Element} - JSX for the MuiAlert component.
 */
export const MuiAlert = forwardRef<HTMLDivElement, PropsType>((props, ref): JSX.Element => {
  const { iconType = 'info', color = 'info', icon, isLink, alignSelfIcon, ...restProps } = props;

  // Selecting the appropriate icon based on the provided iconType
  const iconVariant = icon !== false ? variantIcon[iconType] : false;

  return (
    <CustomAlertStyle
      {...restProps}
      ref={ref}
      icon={iconVariant}
      variant="standard"
      color={color}
      isLink={isLink}
      alignSelfIcon={alignSelfIcon}
      action={
        isLink && (
          <Box>
            <ArrowRightBigIcon />
          </Box>
        )
      }
    >
      {props.children}
    </CustomAlertStyle>
  );
});
